import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { View } from '@amzn/stencil-react-components/layout'
import {
    Controls,
    deserializeFromMarkdown,
    RichTextEditor,
    RichTextEditorProps,
    useEditor,
} from '@amzn/stencil-react-components/rte'
import { TextArea } from '@amzn/stencil-react-components/text-area'

import { Locale } from 'src/models/dto/Locale'

export type ToolbarControls = RichTextEditorProps['controls']

export const SimpleToolbar: ToolbarControls = {
    blockquote: false,
    bulletList: false,
    underline: false,
    link: false,
    heading: false,
    horizontalRule: false,
    keyboardShortcuts: false,
    fullScreen: false,
    clearFormat: false,
    orderedList: false,
    strike: false,
    history: false,
    textAlign: false,
    indent: false,
}

export interface MarkdownEditorProps {
    disabled?: boolean
    title?: string
    value: string
    onChange: React.Dispatch<string>
    id?: string
    dataTestId?: string
    fallbackToTextArea?: boolean
    height?: string | number
    locale?: Locale
    placeholder?: string
    toolbarControls?: Partial<Controls>
}

const defaultHeight = '320px'

export function StencilMarkdownEditor({
    value: mdValue,
    onChange: onMdValueChange,
    id,
    dataTestId,
    height = defaultHeight,
    toolbarControls,
    disabled,
}: MarkdownEditorProps) {
    const [reloadKey, reload] = useReducer((x: number) => x + 1, 0)
    const [defaultHtmlValue, setDefaultHtmlValue] = useState(
        () => deserializeFromMarkdown(mdValue) ?? ''
    )

    const controls = useMemo(
        (): Partial<Controls> => ({
            ...toolbarControls,
            fullScreen: false,
        }),
        [toolbarControls]
    )
    const [editorProps, _editorInstance] = useEditor({ controls })
    const lastMdValue = useRef(mdValue)
    useEffect(() => {
        // TODO temp disable
        if (1 === 1 + 1 && mdValue !== lastMdValue.current) {
            setDefaultHtmlValue(deserializeFromMarkdown(mdValue) ?? '')
            lastMdValue.current = mdValue
            reload()
        }
    }, [mdValue])
    return (
        <View id={id} dataTestId={dataTestId} style={{ minHeight: height }}>
            <RichTextEditor
                key={reloadKey}
                {...editorProps}
                defaultContent={defaultHtmlValue}
                onChange={(editor1) => {
                    onMdValueChange((lastMdValue.current = editor1.getMarkdown()))
                }}
                disabled={disabled}
            />
        </View>
    )
}

function TextAreaMarkdownEditor({
    value: markdownControlled,
    onChange: onMarkdownChange,
    id,
    dataTestId,
    height = defaultHeight,
    placeholder,
    disabled,
}: MarkdownEditorProps) {
    return (
        <TextArea
            placeholder={placeholder ?? 'Markdown Content'}
            value={markdownControlled}
            onChange={(e) => onMarkdownChange(e.target.value)}
            dataTestId={dataTestId}
            id={id}
            height={height}
            disabled={disabled}
        />
    )
}

export function MarkdownEditor(props: MarkdownEditorProps) {
    const { fallbackToTextArea } = props
    const canRenderMarkdownEditor =
        (window as never as Record<string, unknown>).ResizeObserver &&
        (document as never as Record<string, unknown>).elementFromPoint
    if (!canRenderMarkdownEditor || fallbackToTextArea) {
        return <TextAreaMarkdownEditor {...props} />
    }
    /* istanbul ignore next */
    return <StencilMarkdownEditor {...props} />
}
